// src/pages/SkillsPage.js
// Skill Share — residents post what they can offer or need:
// skills, time to volunteer, or help/volunteers wanted.
// Public browsing, no login required. Posting requires sign-in (any role).

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../lib/AuthContext';
import { getSkillListings } from '../lib/db';
import { format } from 'date-fns';
import BottomNav from '../components/layout/BottomNav';
import { HeaderAvatar } from '../components/shared/HeaderAvatar';
import { PageBack } from '../components/shared/PageBack';
import { ReferBanner } from '../components/shared/ReferBanner';
import { SponsorStrip } from '../components/shared/SponsorStrip';

export const SKILL_CATEGORIES = [
  { value: 'all',          label: 'All',            emoji: '✨' },
  { value: 'home_garden',  label: 'Home & Garden',  emoji: '🏡' },
  { value: 'transport',    label: 'Transport',      emoji: '🚗' },
  { value: 'care',         label: 'Care & Company', emoji: '🤝' },
  { value: 'tech',         label: 'Tech Help',      emoji: '💻' },
  { value: 'trades',       label: 'Trades & Repairs', emoji: '🔧' },
  { value: 'tutoring',     label: 'Tutoring & Lessons', emoji: '📚' },
  { value: 'admin',        label: 'Admin & Paperwork', emoji: '📋' },
  { value: 'other',        label: 'Other',          emoji: '💬' },
];

export const SKILL_TYPES = {
  offer_skill:      { label: 'Offering a skill',        emoji: '🎁', color: 'var(--sage-deep)' },
  offer_time:       { label: 'Offering time to volunteer', emoji: '🙋', color: 'var(--sage-deep)' },
  need_skill:       { label: 'Looking for help',         emoji: '🔍', color: 'var(--warm-dark)' },
  need_volunteers:  { label: 'Looking for volunteers',   emoji: '📣', color: 'var(--warm-dark)' },
};

const SkillsPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const townId = localStorage.getItem('gather-town-id') || 'loughrea';
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [category, setCategory] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [search, setSearch] = useState('');

  useEffect(() => {
    getSkillListings(townId)
      .then(setListings)
      .catch(e => console.error('Failed to load skill listings:', e))
      .finally(() => setLoading(false));
  }, []);

  const filtered = listings.filter(l => {
    const matchesCat = category === 'all' || l.category === category;
    const matchesType = typeFilter === 'all' || l.type === typeFilter;
    const matchesSearch = !search ||
      l.title?.toLowerCase().includes(search.toLowerCase()) ||
      l.description?.toLowerCase().includes(search.toLowerCase());
    return matchesCat && matchesType && matchesSearch;
  });

  return (
    <div className="page" style={{ background: 'var(--cream)' }}>

      <div style={{ padding: '52px 20px 0' }}><PageBack fallbackPath="/" /></div>

      <div style={{ ...s.header, padding: '0 20px 12px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <div>
            <h1 style={s.h1}>Skill Share</h1>
            <div style={s.sub}>What we can offer each other, locally</div>
          </div>
          <HeaderAvatar />
        </div>

        <SponsorStrip townId={townId} page="skills" />

        <div style={s.searchBox}>
          <span style={s.searchIcon}>🔍</span>
          <input
            style={s.searchInput}
            placeholder="Search Skill Share..."
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
          {search && <button style={s.clearBtn} aria-label="Clear search" onClick={() => setSearch('')}>✕</button>}
        </div>
      </div>

      {/* Offer/Need toggle */}
      <div style={s.typeScroll}>
        <button
          style={{ ...s.typePill, background: typeFilter === 'all' ? 'var(--sage-dark)' : 'var(--white)', color: typeFilter === 'all' ? 'white' : 'var(--ink)' }}
          onClick={() => setTypeFilter('all')}>
          Everything
        </button>
        {Object.entries(SKILL_TYPES).map(([value, t]) => (
          <button key={value}
            style={{ ...s.typePill, background: typeFilter === value ? 'var(--sage-dark)' : 'var(--white)', color: typeFilter === value ? 'white' : 'var(--ink)' }}
            onClick={() => setTypeFilter(value)}>
            {t.emoji} {t.label}
          </button>
        ))}
      </div>

      {/* Category pills */}
      <div style={s.catScroll}>
        {SKILL_CATEGORIES.map(cat => (
          <button key={cat.value}
            style={{
              ...s.catPill,
              background: category === cat.value ? 'var(--sage-pale)' : 'var(--white)',
              color: 'var(--ink)',
              border: category === cat.value ? '1.5px solid var(--sage-deep)' : '1.5px solid var(--border)',
            }}
            onClick={() => setCategory(cat.value)}>
            {cat.emoji} {cat.label}
          </button>
        ))}
      </div>

      {/* Post CTA */}
      <div style={s.postCtaWrap}>
        <button style={s.postCta} onClick={() => navigate(user ? '/skills/post' : '/login')}>
          + Post something you can offer or need
        </button>
      </div>

      {/* Listings */}
      <div style={s.list}>
        {loading ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: 40 }}>
            <div className="spinner" />
          </div>
        ) : filtered.length === 0 ? (
          <div className="empty-state">
            <div className="empty-state-icon">✨</div>
            <div className="empty-state-title">Nothing here yet</div>
            <div className="empty-state-body">Be the first to post what you can offer or need.</div>
          </div>
        ) : filtered.map(listing => (
          <SkillCard key={listing.id} listing={listing} />
        ))}
      </div>

      <PageBack position="bottom" fallbackPath="/" />
      <ReferBanner />
      <BottomNav />
    </div>
  );
};

const SkillCard = ({ listing }) => {
  const navigate = useNavigate();
  const t = SKILL_TYPES[listing.type] || SKILL_TYPES.offer_skill;
  const cat = SKILL_CATEGORIES.find(c => c.value === listing.category);

  return (
    <div style={s.card} onClick={() => navigate(`/skills/${listing.id}`)}>
      <div style={{ ...s.typeBadge, background: t.color }}>{t.emoji} {t.label}</div>
      <div style={s.cardTitle}>{listing.title}</div>
      {listing.description && <div style={s.cardDesc}>{listing.description}</div>}
      <div style={s.cardMeta}>
        {cat && <span>{cat.emoji} {cat.label}</span>}
        {listing.createdAt?.toDate && (
          <span> · {format(listing.createdAt.toDate(), 'd MMM')}</span>
        )}
      </div>
    </div>
  );
};

const s = {
  header: { padding: '52px 20px 12px' },
  h1: { fontSize: 28, fontWeight: 800, color: 'var(--ink)', marginBottom: 3, letterSpacing: '-0.3px' },
  sub: { fontSize: 14, color: 'var(--muted)', fontWeight: 500, marginBottom: 14 },
  searchBox: {
    display: 'flex', alignItems: 'center', gap: 10,
    background: 'var(--white)', borderRadius: 14, padding: '11px 14px',
    border: '1.5px solid var(--border)',
  },
  searchIcon: { fontSize: 16, flexShrink: 0 },
  searchInput: {
    flex: 1, border: 'none', outline: 'none', background: 'transparent',
    fontSize: 15, fontWeight: 500, color: 'var(--ink)', fontFamily: 'Nunito, sans-serif',
  },
  clearBtn: { background: 'none', border: 'none', color: 'var(--muted)', fontSize: 14, cursor: 'pointer', padding: '0 4px' },
  typeScroll: { display: 'flex', gap: 8, overflowX: 'auto', padding: '10px 20px 4px', scrollbarWidth: 'none' },
  typePill: {
    flexShrink: 0, padding: '8px 14px', borderRadius: 100,
    fontSize: 12, fontWeight: 700, cursor: 'pointer', whiteSpace: 'nowrap',
    border: 'none', fontFamily: 'Nunito, sans-serif',
  },
  catScroll: { display: 'flex', gap: 8, overflowX: 'auto', padding: '6px 20px 10px', scrollbarWidth: 'none' },
  catPill: {
    flexShrink: 0, padding: '8px 14px', borderRadius: 100,
    fontSize: 12, fontWeight: 700, cursor: 'pointer', whiteSpace: 'nowrap',
    fontFamily: 'Nunito, sans-serif',
  },
  postCtaWrap: { padding: '4px 20px 8px' },
  postCta: {
    width: '100%', padding: '13px 16px', borderRadius: 14,
    background: 'var(--sage-deep)', color: 'white', border: 'none',
    fontSize: 14, fontWeight: 700, cursor: 'pointer', fontFamily: 'Nunito, sans-serif',
  },
  list: { padding: '4px 18px 24px', display: 'flex', flexDirection: 'column', gap: 10 },
  card: {
    background: 'var(--white)', borderRadius: 16, padding: 16,
    border: '1.5px solid var(--border)', cursor: 'pointer',
  },
  typeBadge: {
    display: 'inline-block', color: 'white', fontSize: 11, fontWeight: 700,
    borderRadius: 100, padding: '4px 10px', marginBottom: 8,
  },
  cardTitle: { fontSize: 16, fontWeight: 800, color: 'var(--ink)', marginBottom: 4 },
  cardDesc: { fontSize: 13.5, color: 'var(--muted)', fontWeight: 500, lineHeight: 1.5, marginBottom: 8 },
  cardMeta: { fontSize: 12, color: 'var(--muted)', fontWeight: 600 },
};

export default SkillsPage;
